import React from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { ArrowLeft } from "lucide-react";

function LegalShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo size={28} />
            <span className="font-serif-heading text-xl">AI Bridal Manager™</span>
          </Link>
          <Link to="/" className="text-sm text-neutral-600 hover:text-primary inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Torna al sito
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <span className="label-uppercase text-primary">Documento legale</span>
        <h1 className="font-serif-heading text-4xl sm:text-5xl mt-2">{title}</h1>
        {subtitle && <p className="mt-3 text-neutral-600">{subtitle}</p>}
        <div className="mt-10 space-y-6 text-[15px] leading-relaxed text-neutral-800 [&_h2]:font-serif-heading [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-2 [&_h3]:font-medium [&_h3]:mt-6 [&_h3]:mb-1 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_li]:mb-1 [&_strong]:font-semibold">
          {children}
        </div>
        <div className="mt-16 pt-6 border-t border-border text-xs text-neutral-500">
          Versione documento: <strong>1.0 · Aprile 2026</strong> · Per richieste di rettifica, cancellazione o portabilità dei dati: contattaci all'indirizzo email indicato nei contatti.
        </div>
      </main>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      subtitle="Informativa ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 (GDPR)"
    >
      <h2>1. Titolare del trattamento</h2>
      <p>
        Titolare del trattamento dei dati personali è <strong>il professionista intestatario dell'account</strong> AI Bridal Manager™ (di seguito "Utente Professionista").
        AI Bridal Manager™ opera come <strong>Responsabile del trattamento</strong> per conto del Titolare ai sensi dell'art. 28 GDPR.
      </p>

      <h2>2. Dati raccolti</h2>
      <h3>2.1 Dati dell'Utente Professionista</h3>
      <ul>
        <li>Nome, cognome, email, telefono</li>
        <li>Password (memorizzata in forma cifrata con bcrypt)</li>
        <li>Indirizzo IP e user-agent al momento dell'accesso</li>
        <li>Dati di pagamento (gestiti esclusivamente da Stripe — non memorizziamo carte di credito)</li>
        <li>Log delle azioni amministrative</li>
      </ul>
      <h3>2.2 Dati dei clienti finali (sposi/spose)</h3>
      <ul>
        <li>Nome, contatti, data dell'evento, location</li>
        <li>Note libere inserite dall'Utente Professionista</li>
        <li>Schede tecniche makeup/hair (caratteristiche fisiche, stile desiderato)</li>
        <li>Foto delle prove (caricate dall'Utente Professionista)</li>
        <li>Audit trail di firma elettronica (IP, timestamp, hash documento) per i contratti firmati digitalmente</li>
      </ul>

      <h2>3. Finalità e basi giuridiche</h2>
      <ul>
        <li><strong>Erogazione del servizio</strong> (art. 6.1.b GDPR — esecuzione del contratto): gestione account, schede clienti, preventivi, contratti, calendario, timeline.</li>
        <li><strong>Sicurezza e prevenzione frodi</strong> (art. 6.1.f — legittimo interesse): brute-force protection, audit log, monitoraggio accessi anomali.</li>
        <li><strong>Adempimenti fiscali e contabili</strong> (art. 6.1.c — obbligo legale): conservazione transazioni Stripe.</li>
        <li><strong>Firma elettronica semplice</strong> (art. 6.1.b): verifica OTP, registrazione IP/UA per contratti firmati.</li>
      </ul>

      <h2>4. Conservazione</h2>
      <ul>
        <li>Account Utente Professionista: per tutta la durata del rapporto + 24 mesi dalla cessazione.</li>
        <li>Dati clienti: gestiti dall'Utente Professionista, eliminabili in qualsiasi momento.</li>
        <li>Dati di pagamento: 10 anni per obblighi fiscali (art. 2220 c.c.).</li>
        <li>Audit log firme: 10 anni dalla firma per validità probatoria.</li>
      </ul>

      <h2>5. Destinatari</h2>
      <ul>
        <li>Provider infrastruttura cloud (server europei o con clausole contrattuali standard).</li>
        <li>MongoDB Atlas / hosting database (con DPA in essere).</li>
        <li>Stripe Inc. per i pagamenti (Privacy Policy: stripe.com/privacy).</li>
        <li>Mai venduti o ceduti a terzi per finalità di marketing.</li>
      </ul>

      <h2>6. Trasferimenti extra-UE</h2>
      <p>
        Eventuali trasferimenti di dati al di fuori dell'Unione Europea avvengono esclusivamente verso paesi con decisione di adeguatezza
        della Commissione Europea o sulla base delle Clausole Contrattuali Standard (SCC).
      </p>

      <h2>7. Diritti dell'interessato</h2>
      <p>Hai diritto, in qualsiasi momento, a:</p>
      <ul>
        <li><strong>Accesso</strong> (art. 15) — sapere quali dati abbiamo</li>
        <li><strong>Rettifica</strong> (art. 16) — correggere dati inesatti</li>
        <li><strong>Cancellazione</strong> (art. 17 — "diritto all'oblio") — direttamente da <em>Impostazioni → Privacy → Elimina account</em></li>
        <li><strong>Portabilità</strong> (art. 20) — scaricare tutti i tuoi dati in formato JSON da <em>Impostazioni → Privacy → Scarica i miei dati</em></li>
        <li><strong>Limitazione e opposizione</strong> (artt. 18, 21)</li>
        <li><strong>Reclamo al Garante Privacy</strong> (www.garanteprivacy.it)</li>
      </ul>

      <h2>8. Sicurezza</h2>
      <ul>
        <li>Connessioni cifrate HTTPS/TLS</li>
        <li>Password hashate con bcrypt (cost 12)</li>
        <li>Cookie di sessione httpOnly + SameSite</li>
        <li>Brute-force protection (5 tentativi → blocco 15 minuti)</li>
        <li>Token JWT con scadenza 1 ora (access) / 7 giorni (refresh)</li>
      </ul>

      <h2>9. Modifiche</h2>
      <p>
        Eventuali aggiornamenti a questa informativa saranno comunicati via email o tramite avviso in-app. La versione corrente è
        consultabile in fondo a questa pagina.
      </p>
    </LegalShell>
  );
}

export function CookiePage() {
  return (
    <LegalShell
      title="Cookie Policy"
      subtitle="Informativa sull'utilizzo dei cookie ai sensi della direttiva ePrivacy"
    >
      <h2>1. Cosa sono i cookie</h2>
      <p>
        I cookie sono piccoli file di testo che i siti web visitati salvano sul tuo dispositivo per consentire il corretto funzionamento del servizio
        e per memorizzare alcune preferenze.
      </p>

      <h2>2. Cookie utilizzati da AI Bridal Manager™</h2>
      <p>Utilizziamo <strong>esclusivamente cookie tecnici</strong> e di sessione, indispensabili per il funzionamento dell'applicazione. Per questi cookie non è richiesto il consenso esplicito.</p>

      <h3>Cookie tecnici (sempre attivi)</h3>
      <ul>
        <li><strong>access_token</strong> — JWT di autenticazione · httpOnly · scadenza 1 ora</li>
        <li><strong>refresh_token</strong> — JWT di refresh sessione · httpOnly · scadenza 7 giorni</li>
        <li><strong>abm_access_token</strong> (localStorage) — fallback per browser che bloccano cookie cross-site</li>
        <li><strong>abm_cookie_consent</strong> (localStorage) — memorizza la chiusura del banner cookie</li>
      </ul>

      <h2>3. Cookie di terze parti</h2>
      <p>
        Quando completi un pagamento, vieni reindirizzato sul checkout sicuro di Stripe. In quella sede Stripe può utilizzare propri cookie tecnici e di
        prevenzione frodi. Maggiori informazioni: <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noreferrer" className="text-primary underline">stripe.com/cookies-policy</a>.
      </p>

      <h2>4. Nessun cookie di profilazione</h2>
      <p>
        AI Bridal Manager™ <strong>non utilizza cookie di profilazione, marketing, advertising, social plugin o analytics di tracciamento</strong>.
        Nessun dato di navigazione viene venduto o ceduto a terzi.
      </p>

      <h2>5. Come disabilitare i cookie</h2>
      <p>
        Puoi disabilitare i cookie dalle impostazioni del tuo browser. Disabilitando i cookie tecnici l'accesso all'applicazione non sarà più possibile,
        in quanto la sessione di autenticazione non potrà essere mantenuta.
      </p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      title="Termini di Servizio"
      subtitle="Condizioni generali d'uso di AI Bridal Manager™"
    >
      <h2>1. Oggetto</h2>
      <p>
        I presenti Termini disciplinano l'utilizzo della piattaforma SaaS <strong>AI Bridal Manager™</strong> (di seguito "Servizio"), che mette a
        disposizione dei professionisti del settore wedding/makeup/hair strumenti gestionali per clienti, preventivi, contratti, calendario e firma
        elettronica.
      </p>

      <h2>2. Accesso e registrazione</h2>
      <ul>
        <li>L'accesso è riservato a maggiorenni e richiede la creazione di un account.</li>
        <li>Le credenziali sono personali; l'Utente è responsabile della loro custodia.</li>
        <li>L'Utente garantisce la veridicità dei dati inseriti.</li>
      </ul>

      <h2>3. Piani e pagamenti</h2>
      <ul>
        <li>Il piano <strong>FREE</strong> consente l'utilizzo limitato del Servizio.</li>
        <li>I piani <strong>BASIC</strong> e <strong>PRO</strong> sono a sottoscrizione mensile, gestiti tramite Stripe.</li>
        <li>Il rinnovo è automatico salvo disdetta dalla sezione Piano.</li>
        <li>Eventuali rimborsi sono valutati caso per caso secondo i diritti del consumatore.</li>
      </ul>

      <h2>4. Contenuti dell'Utente</h2>
      <p>
        L'Utente Professionista è l'unico titolare e responsabile dei dati dei propri clienti finali (informazioni anagrafiche, foto, contratti).
        AI Bridal Manager™ agisce come Responsabile del trattamento ai sensi dell'art. 28 GDPR.
        L'Utente garantisce di aver raccolto tutti i consensi necessari dai propri clienti prima di inserirne i dati nella piattaforma.
      </p>

      <h2>5. Utilizzi vietati</h2>
      <ul>
        <li>Caricare contenuti illeciti, lesivi o protetti da diritti di terzi senza autorizzazione</li>
        <li>Tentare di compromettere la sicurezza o l'integrità del Servizio</li>
        <li>Reverse engineering, scraping massivo, riutilizzo del codice</li>
        <li>Usare il Servizio per finalità diverse da quelle gestionali professionali</li>
      </ul>

      <h2>6. Disponibilità del Servizio</h2>
      <p>
        Il Servizio è erogato in modalità "as is". Pur impegnandoci nella massima continuità operativa, non garantiamo l'assenza di interruzioni
        per manutenzioni programmate, cause di forza maggiore o malfunzionamenti dei provider terzi.
      </p>

      <h2>7. Limitazione di responsabilità</h2>
      <p>
        Nei limiti consentiti dalla legge, la responsabilità di AI Bridal Manager™ verso l'Utente è limitata all'importo pagato dall'Utente
        nei 12 mesi precedenti l'evento contestato. Nessuna responsabilità per danni indiretti, perdita di clientela o mancato guadagno.
      </p>

      <h2>8. Recesso e cancellazione account</h2>
      <p>
        L'Utente può cancellare l'account in qualsiasi momento da <em>Impostazioni → Privacy → Elimina account</em>. La cancellazione è
        immediata e irreversibile (i dati di pagamento vengono conservati 10 anni per obblighi fiscali).
      </p>

      <h2>9. Modifiche ai Termini</h2>
      <p>
        Eventuali modifiche sostanziali saranno comunicate con preavviso di 30 giorni via email. La continuazione nell'uso del Servizio
        dopo le modifiche costituisce accettazione delle stesse.
      </p>

      <h2>10. Legge applicabile e foro competente</h2>
      <p>
        I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia è competente in via esclusiva il foro del
        consumatore o, in mancanza, il foro di residenza del Titolare.
      </p>
    </LegalShell>
  );
}
